import { Fragment, useEffect, useState } from "react";
import {
  webhookLog as api,
  type WebhookEndpoint,
  type WebhookEvent,
  type WebhookEventStatus,
} from "../api";

const STATUS_FILTERS: { value: "" | WebhookEventStatus; label: string }[] = [
  { value: "", label: "All" },
  { value: "processed", label: "Processed" },
  { value: "ignored", label: "Ignored" },
  { value: "failed", label: "Failed" },
];

const STATUS_PILL: Record<WebhookEventStatus, string> = {
  received: "status-submitted",
  processed: "status-executed",
  ignored: "status-cancelled",
  failed: "status-rejected",
};

export function Webhooks() {
  const [events, setEvents] = useState<WebhookEvent[] | null>(null);
  const [endpoints, setEndpoints] = useState<WebhookEndpoint[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<"" | WebhookEventStatus>("");
  const [sourceFilter, setSourceFilter] = useState<string>("");
  const [expanded, setExpanded] = useState<number | null>(null);
  const [clearing, setClearing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [tab, setTab] = useState<"deliveries" | "endpoints">("deliveries");

  function reload() {
    setError(null);
    api
      .list(500)
      .then((d) => {
        setEvents(d.events);
        setTotal(d.total);
      })
      .catch((e) => setError(e.message));
  }

  useEffect(() => {
    reload();
    api.endpoints().then((d) => setEndpoints(d.endpoints)).catch(() => {});
  }, []);

  async function clearLog() {
    setClearing(true);
    setError(null);
    try {
      await api.clear();
      setExpanded(null);
      setConfirming(false);
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setClearing(false);
    }
  }

  const shown = (events ?? []).filter(
    (e) =>
      (!statusFilter || e.status === statusFilter) &&
      (!sourceFilter || e.source === sourceFilter),
  );
  const sources = Array.from(new Set((events ?? []).map((e) => e.source)));

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <span className="eyebrow">Integrations</span>
          <h1>Webhooks</h1>
        </div>
      </header>

      <nav className="tabs">
        <button
          type="button"
          className={tab === "deliveries" ? "tab-link active" : "tab-link"}
          onClick={() => setTab("deliveries")}
        >
          Deliveries{total > 0 ? ` (${total})` : ""}
        </button>
        <button
          type="button"
          className={tab === "endpoints" ? "tab-link active" : "tab-link"}
          onClick={() => setTab("endpoints")}
        >
          Endpoints{endpoints.length > 0 ? ` (${endpoints.length})` : ""}
        </button>
      </nav>

      {error && <div className="banner-error">{error}</div>}

      {tab === "endpoints" && (
        <section className="webhook-endpoints">
          <p className="muted">
            Configure these as webhook endpoints in Quantfolio. Each has a
            production and a test URL — copy the one for the environment you're
            wiring up.
          </p>
          {endpoints.length === 0 ? (
            <div className="muted">Loading…</div>
          ) : (
            <div className="mcp-list">
              {endpoints.map((ep) => (
                <EndpointCard key={ep.key} ep={ep} />
              ))}
            </div>
          )}
        </section>
      )}

      {tab === "deliveries" && (
        <>
          <p className="muted">
            Every inbound webhook delivery is recorded here — the board across all
            endpoints and event types. Clearing wipes this log only; it does not
            affect existing pre-order checks.
          </p>

          <div className="webhook-toolbar">
          <div className="webhook-filters">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value || "all"}
                type="button"
                className={statusFilter === f.value ? "chip active" : "chip"}
                onClick={() => setStatusFilter(f.value)}
              >
                {f.label}
              </button>
            ))}
            {sources.length > 1 && (
              <>
                <span className="webhook-filter-sep" aria-hidden="true" />
                <button
                  type="button"
                  className={sourceFilter === "" ? "chip active" : "chip"}
                  onClick={() => setSourceFilter("")}
                >
                  All sources
                </button>
                {sources.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={sourceFilter === s ? "chip active" : "chip"}
                    onClick={() => setSourceFilter(s)}
                  >
                    {s}
                  </button>
                ))}
              </>
            )}
          </div>
            <div className="webhook-actions">
              <button
                type="button"
                className="icon-btn"
                onClick={reload}
                title="Refresh"
                aria-label="Refresh"
              >
                <RefreshIcon />
              </button>
              {confirming ? (
                <span className="webhook-confirm">
                  Clear {total}?
                  <button
                    type="button"
                    className="icon-btn danger"
                    disabled={clearing}
                    onClick={clearLog}
                    title="Confirm clear"
                    aria-label="Confirm clear"
                  >
                    <CheckIcon />
                  </button>
                  <button
                    type="button"
                    className="icon-btn"
                    disabled={clearing}
                    onClick={() => setConfirming(false)}
                    title="Cancel"
                    aria-label="Cancel"
                  >
                    <CloseIcon />
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  className="icon-btn danger"
                  disabled={total === 0}
                  onClick={() => setConfirming(true)}
                  title="Clear log"
                  aria-label="Clear log"
                >
                  <TrashIcon />
                </button>
              )}
            </div>
          </div>

          {events === null ? (
            <div className="muted">Loading…</div>
          ) : shown.length === 0 ? (
            <div className="empty">
              <p>No matching webhooks{total === 0 ? " received yet" : ""}.</p>
            </div>
          ) : (
            <table className="table">
          <thead>
            <tr>
              <th>Received</th>
              <th>Source</th>
              <th>Event</th>
              <th>Flow instance</th>
              <th>Signature</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((e) => (
              <Fragment key={e.id}>
                <tr
                  className="webhook-row expandable"
                  onClick={() => setExpanded(expanded === e.id ? null : e.id)}
                >
                  <td className="mono">{formatDate(e.received_at)}</td>
                  <td>
                    <span className="pill source-pill">{e.source}</span>
                  </td>
                  <td className="strong">{e.event_type ?? "—"}</td>
                  <td className="mono">{e.external_id ?? "—"}</td>
                  <td>
                    <SignaturePill valid={e.signature_valid} />
                  </td>
                  <td>
                    <span className={`pill ${STATUS_PILL[e.status]}`}>{e.status}</span>
                  </td>
                </tr>
                {expanded === e.id && (
                  <tr>
                    <td className="webhook-detail-cell" colSpan={6}>
                      <div className="webhook-detail">
                        <div className="meta">
                          <span>
                            source <code>{e.source}</code>
                          </span>
                          <span>
                            delivery id <code>{e.delivery_id ?? "—"}</code>
                          </span>
                          {e.processed_at && <span>processed {formatDate(e.processed_at)}</span>}
                        </div>
                        {e.error && <div className="banner-error">{e.error}</div>}
                        <span className="webhook-expand-hint">Payload</span>
                        <pre className="webhook-payload">{prettyJson(e.payload)}</pre>
                        {e.headers && (
                          <>
                            <span className="webhook-expand-hint">
                              Request headers (inspect the signature scheme here)
                            </span>
                            <pre className="webhook-payload">{prettyJson(e.headers)}</pre>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
          )}
        </>
      )}
    </div>
  );
}

function EndpointCard({ ep }: { ep: WebhookEndpoint }) {
  return (
    <section className="mcp-server">
      <header className="mcp-server-head">
        <div>
          <h2>{ep.label}</h2>
          <p className="mcp-server-instructions">{ep.description}</p>
        </div>
        <span className={`pill ${ep.secretConfigured ? "sig-ok" : "sig-none"}`}>
          {ep.secretConfigured ? "HMAC secret set" : "No secret (unsigned)"}
        </span>
      </header>
      <dl className="mcp-meta">
        <CopyRow label="Production URL" value={ep.productionUrl} />
        <CopyRow label="Test URL" value={ep.testUrl} />
        <Row label="Action" value={ep.actionLabel} />
        <Row label="Signature header" value={ep.signatureHeader} mono />
        <Row label="Secret env var" value={ep.secretEnv} mono />
      </dl>
    </section>
  );
}

function RefreshIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12a9 9 0 1 1-3-6.7L21 8" />
      <path d="M21 3v5h-5" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 6h18" />
      <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  );
}

function SignaturePill({ valid }: { valid: number | null }) {
  if (valid === null) return <span className="pill sig-none">Unsigned</span>;
  if (valid === 1) return <span className="pill sig-ok">Verified</span>;
  return <span className="pill sig-bad">Invalid</span>;
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="info-row">
      <dt>{label}</dt>
      <dd className={mono ? "mono" : undefined}>{value}</dd>
    </div>
  );
}

function CopyRow({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* noop */
    }
  }
  return (
    <div className="info-row">
      <dt>{label}</dt>
      <dd className="mono">
        {value}
        <button type="button" className="copy-btn" onClick={copy} aria-label={`Copy ${label}`}>
          {copied ? "Copied" : "Copy"}
        </button>
      </dd>
    </div>
  );
}

function prettyJson(raw: string | null): string {
  if (!raw) return "(empty body)";
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

function formatDate(iso: string): string {
  const d = new Date(iso.includes("T") ? iso : iso.replace(" ", "T") + "Z");
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}
